import { HttpErrorResponse } from '@angular/common/http';

function firstExceptionErrorType(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const messages = (o['messages'] ?? o['Messages']) as unknown;
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const first = messages[0];
  if (!first || typeof first !== 'object') return null;
  const rec = first as Record<string, unknown>;
  const t = rec['errorType'] ?? rec['ErrorType'];
  return typeof t === 'string' && t.trim() ? t.trim() : null;
}

function firstExceptionMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const valid = o['valid'] ?? o['Valid'];
  const messages = (o['messages'] ?? o['Messages']) as unknown;
  if (valid !== false || !Array.isArray(messages) || messages.length === 0) {
    return null;
  }
  const first = messages[0];
  if (!first || typeof first !== 'object') return null;
  const rec = first as Record<string, unknown>;
  const msg = rec['message'] ?? rec['Message'];
  if (typeof msg === 'string' && msg.trim()) {
    return msg.trim();
  }
  return null;
}

/**
 * Human-readable message for client API failures (gateway exception shape, Forbidden shape, etc.).
 */
export function getClientApiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof HttpErrorResponse)) {
    if (err instanceof Error && err.message.trim()) {
      return err.message.trim();
    }
    return fallback;
  }

  const status = err.status;
  const body = err.error;

  if (status === 401) {
    return 'Authentication required. Please log in again.';
  }

  if (status === 403) {
    if (body && typeof body === 'object') {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string' && m.trim()) {
        return m.trim();
      }
    }
    return 'Access denied.';
  }

  if (status === 404) {
    const fromEx = firstExceptionMessage(body);
    if (fromEx) return fromEx;
    if (body && typeof body === 'object') {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string' && m.trim()) {
        return m.trim();
      }
    }
    return fallback;
  }

  if (status === 400 || status === 409) {
    const fromEx = firstExceptionMessage(body);
    if (fromEx) return fromEx;
  }

  if (status >= 500) {
    return 'Server error. Please try again later.';
  }

  if (status === 0) {
    const net = err.message?.trim();
    return net || 'Network error. Please check your connection.';
  }

  if (body && typeof body === 'object') {
    const m = (body as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) {
      return m.trim();
    }
  }

  const msg = err.message?.trim();
  return msg || fallback;
}

/** Gateway 409 with ErrorType PossibleDuplicate (name+DOB match; user may confirm and retry). */
export function isPossibleDuplicateMemberError(err: unknown): boolean {
  return err instanceof HttpErrorResponse
    && err.status === 409
    && firstExceptionErrorType(err.error) === 'PossibleDuplicate';
}

export interface PossibleDuplicateMatch {
  memberId: number;
  cCode: string;
}

/** Structured Member ID + cCode pairs from PossibleDuplicate 409 messages. */
export function possibleDuplicateMatchesFromError(err: unknown): PossibleDuplicateMatch[] {
  if (!isPossibleDuplicateMemberError(err)) return [];
  const body = (err as HttpErrorResponse).error;
  if (!body || typeof body !== 'object') return [];
  const messages = ((body as Record<string, unknown>)['messages']
    ?? (body as Record<string, unknown>)['Messages']) as unknown;
  if (!Array.isArray(messages)) return [];

  const matches: PossibleDuplicateMatch[] = [];
  const seen = new Set<string>();
  for (const item of messages) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const memberId = rec['memberId'] ?? rec['MemberId'];
    const cCode = rec['cCode'] ?? rec['CCode'];
    const id = typeof memberId === 'number' ? memberId : Number(memberId);
    if (!Number.isFinite(id) || id <= 0 || typeof cCode !== 'string' || !cCode.trim()) continue;
    const key = `${id}|${cCode.trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    matches.push({ memberId: id, cCode: cCode.trim() });
  }
  return matches;
}

/** Member ID list from "This member may already exist (Member ID 123, 456)". */
export function possibleDuplicateSubIdsFromError(err: unknown): string | null {
  if (!isPossibleDuplicateMemberError(err)) return null;
  const fromMatches = possibleDuplicateMatchesFromError(err)
    .map((m) => m.memberId)
    .filter((id, i, all) => all.indexOf(id) === i);
  if (fromMatches.length > 0) return fromMatches.join(', ');
  const msg = firstExceptionMessage((err as HttpErrorResponse).error);
  const match = msg?.match(/\(Member ID ([0-9, ]+)\)/i);
  return match?.[1]?.replace(/\s+/g, ' ').trim() || null;
}

export function memberDetailRouterLink(mCode: string, cCode: string, memberId: number): string[] {
  return ['/group', mCode, 'subgroup', cCode, 'members', String(memberId)];
}
